import React, { useState, useEffect, useRef } from 'react'
// The reads and actions are shared with the phone Receiving screen (features/receiving/receiving.js,
// docs/mobile-pwa-plan.md "One copy of every action").
import { loadUnlinkedReceipts, uploadReceipt, deleteReceipt, receiptUrl, receiptIsPdf } from '../features/receiving/receiving';
import { desktopUi } from '../features/desktopUi';

export default function ReceivingPage() {

  // State for file uploads
  const [selectedFile, setSelectedFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState(null)

  // Removed: uploadedBy state and input field

  // State to show unlinked docs
  const [unlinkedDocs, setUnlinkedDocs] = useState([])
  const [docsLoading, setDocsLoading] = useState(false)
  const [docsError, setDocsError] = useState(null)

  // State for document preview
  const [previewDoc, setPreviewDoc] = useState(null)

  // File input reference (for resetting after upload)
  const fileInputRef = useRef(null)

  // Fetch unlinked docs once at mount
  useEffect(() => {
    loadUnlinkedDocs()
  }, [])

  async function loadUnlinkedDocs() {
    setDocsLoading(true)
    setDocsError(null)
    try {
      setUnlinkedDocs(await loadUnlinkedReceipts())
    } catch (err) {
      setDocsError(err.message)
    } finally {
      setDocsLoading(false)
    }
  }

  // Handle user selecting a file
  function onFileChange(e) {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0])
    }
  }

  // Main upload flow
  async function handleUpload() {
    const result = await uploadReceipt({
      file: selectedFile, ui: desktopUi, setBusy: setUploading, onStart: () => setUploadError(null),
    })
    if (result.cancelled) return
    setUploadError(result.error || null)
    if (result.reload) {
      // Clear selected file
      setSelectedFile(null)
      if (fileInputRef.current) {
        fileInputRef.current.value = null
      }
      // Refresh the unlinked docs list
      loadUnlinkedDocs()
    }
  }

  // Handle preview
  function handlePreview(doc) {
    setPreviewDoc(doc)
  }

  // Handle delete (with extra confirm popup)
  async function handleDelete(docId) {
    const result = await deleteReceipt({ docId, ui: desktopUi })
    if (result.reload) {
      // Refresh the list of docs
      loadUnlinkedDocs()
      // Clear the preview if it's the same doc
      if (previewDoc && previewDoc.receipt_document_id === docId) {
        setPreviewDoc(null)
      }
    }
  }

  // Render the inline preview at the bottom
  const renderPreview = () => {
    if (!previewDoc) return null

    const docURL = receiptUrl(previewDoc)
    const isPDF = receiptIsPdf(previewDoc)

    return (
      <div style={{ marginTop: "2rem" }}>
        <h4>Document Preview</h4>
        <div style={{ marginBottom: "0.5rem" }}>
          <strong>
            Document #{previewDoc.receipt_document_id} - {previewDoc.original_filename}
          </strong>
        </div>
        {isPDF ? (
          <embed
            src={docURL}
            type="application/pdf"
            width="100%"
            height="800px"
          />
        ) : (
          <img
            src={docURL}
            alt={previewDoc.original_filename}
            style={{ maxWidth: "100%", height: "auto" }}
          />
        )}
      </div>
    )
  }

  return (
    <div className="container my-4">
      <h3>Receiving Page</h3>
      <p>
        This page lets you upload receiving documents (images/PDFs) to be stored as 
        unlinked documents. Managers will later associate them with purchase orders.
      </p>

      {/* Upload Form */}
      <div className="card mb-4">
        <div className="card-body">
          <h5>Upload New Document</h5>

          {/* Removed the "Your Name (Optional)" input field */}

          <div className="mb-3">
            <label className="form-label">Select a File</label>
            <input
              type="file"
              className="form-control"
              onChange={onFileChange}
              ref={fileInputRef}
            />
          </div>

          {selectedFile && (
            <div className="mb-2">
              <strong>Selected:</strong> {selectedFile.name}
            </div>
          )}

          <button
            className="btn btn-primary"
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
          >
            {uploading ? "Uploading..." : "Upload Document"}
          </button>

          {uploadError && (
            <div className="alert alert-danger mt-3">
              {uploadError}
            </div>
          )}
        </div>
      </div>

      {/* List Unlinked Documents */}
      <div className="card">
        <div className="card-body">
          <h5>Unlinked Documents</h5>
          {docsLoading && <div>Loading documents...</div>}
          {docsError && (
            <div className="alert alert-danger">{docsError}</div>
          )}

          {!docsLoading && unlinkedDocs.length === 0 && (
            <div>No unlinked documents found.</div>
          )}

          {!docsLoading && unlinkedDocs.length > 0 && (
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Filename</th>
                  <th>Uploaded By</th>
                  <th>Uploaded At</th>
                  <th>S3 Key</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {unlinkedDocs.map(doc => (
                  <tr key={doc.receipt_document_id}>
                    <td>{doc.receipt_document_id}</td>
                    <td>{doc.original_filename}</td>
                    <td>{doc.uploaded_by || "N/A"}</td>
                    <td>{doc.uploaded_at || "N/A"}</td>
                    <td>{doc.s3_key}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-secondary me-3"
                        onClick={() => handlePreview(doc)}
                      >
                        Preview
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(doc.receipt_document_id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Inline Document Preview at the Bottom */}
      {renderPreview()}
    </div>
  )
}
